import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  useAuth,
  defaultPermissions,
} from "../../hooks/AuthenticationProvider";
import {
  collection,
  doc,
  getDoc,
  deleteDoc,
  setDoc,
  getFirestore,
} from "firebase/firestore";
import { ArrowRight, Building2, Home } from "lucide-react";

const FirstTimeSetup = () => {
  const { user, defaultPermissions, isLoading } = useAuth();
  const [paidResDashboardCount, setPaidResDashboardCount] = useState(0);
  const [paidBedCount, setPaidBedCount] = useState(0);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const db = getFirestore();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    organizationName: "",
    moveInFee: 0,
    rentSchedule: "",
    rentDueDay: "",
    meetingRequirements: "",
    is90n90: false,
    numberOfHouses: 1,
    houses: [
      {
        name: "",
        fee: 0,
        color: "",
        floors: [
          {
            id: 1,
            rooms: [
              {
                id: 1,
                name: "Room 1",
                beds: [{ id: "1A", resident: null }],
              },
            ],
          },
        ],
      },
    ],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login");
      return;
    }

    if (user) {
      checkExistingSetup();
    }
  }, [user, isLoading]);

  const checkExistingSetup = async () => {
    if (!user) return;

    const organizationRef = doc(db, "organizations", user.uid);
    const organizationDoc = await getDoc(organizationRef);

    if (organizationDoc.exists()) {
      navigate("/dashboard");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "numberOfHouses") {
      const numberOfHouses = parseInt(value) || 1;
      setFormData((prev) => ({
        ...prev,
        numberOfHouses,
        houses: Array(numberOfHouses)
          .fill(null)
          .map(
            (_, index) =>
              prev.houses[index] || {
                name: "",
                fee: 0,
                color: "",
                floors: [
                  {
                    id: 1,
                    rooms: [
                      {
                        id: 1,
                        name: "Room 1",
                        beds: [{ id: "1A", resident: null }],
                      },
                    ],
                  },
                ],
              }
          ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleFloorChange = (houseIndex, numberOfFloors) => {
    setFormData((prev) => ({
      ...prev,
      houses: prev.houses.map((house, i) => {
        if (i !== houseIndex) return house;

        const newFloors = Array(numberOfFloors)
          .fill(null)
          .map((_, floorIndex) => ({
            id: floorIndex + 1,
            rooms: house.floors[floorIndex]?.rooms || [
              {
                id: 1,
                name: `Room 1`,
                beds: [{ id: `${floorIndex + 1}A`, resident: null }],
              },
            ],
          }));

        return {
          ...house,
          floors: newFloors,
        };
      }),
    }));
  };

  const handleRoomChange = (houseIndex, floorIndex, numberOfRooms) => {
    setFormData((prev) => ({
      ...prev,
      houses: prev.houses.map((house, i) => {
        if (i !== houseIndex) return house;

        const newFloors = house.floors.map((floor, fi) => {
          if (fi !== floorIndex) return floor;

          const newRooms = Array(numberOfRooms)
            .fill(null)
            .map((_, roomIndex) => ({
              id: roomIndex + 1,
              name: `Room ${roomIndex + 1}`,
              beds: floor.rooms[roomIndex]?.beds || [
                {
                  id: `${floor.id}${String.fromCharCode(65 + roomIndex)}`,
                  resident: null,
                },
              ],
            }));

          return {
            ...floor,
            rooms: newRooms,
          };
        });

        return {
          ...house,
          floors: newFloors,
        };
      }),
    }));
  };

  useEffect(() => {
    if (user) {
      checkExistingSetup();
      fetchPaidBedCount();
    }
  }, [user]);

  const fetchPaidBedCount = async () => {
    if (!user?.email) return;

    try {
      const pendingUserRef = doc(db, "pendingUsers", user.email);
      const pendingUserDoc = await getDoc(pendingUserRef);

      if (pendingUserDoc.exists()) {
        const data = pendingUserDoc.data();
        setPaidBedCount(data.paidBedCount || 0);
        setPaidResDashboardCount(data.paidResDashboardCount || 0);
      }
    } catch (error) {
      console.error("Error fetching paid counts:", error);
    }
  };

  // Add validation helper
  const getTotalBeds = (houses) => {
    return houses.reduce((total, house) => {
      return (
        total +
        house.floors.reduce((floorTotal, floor) => {
          return (
            floorTotal +
            floor.rooms.reduce((roomTotal, room) => {
              return roomTotal + room.beds.length;
            }, 0)
          );
        }, 0)
      );
    }, 0);
  };

  const handleBedChange = (houseIndex, floorIndex, roomIndex, numberOfBeds) => {
    setFormData((prev) => {
      const newHouses = [...prev.houses];
      const currentTotalBeds = getTotalBeds(prev.houses);
      const currentRoomBeds =
        prev.houses[houseIndex].floors[floorIndex].rooms[roomIndex].beds.length;
      const bedDifference = numberOfBeds - currentRoomBeds;

      // Rest of the existing bed change logic
      newHouses[houseIndex] = {
        ...newHouses[houseIndex],
        floors: newHouses[houseIndex].floors.map((floor, fi) => {
          if (fi !== floorIndex) return floor;

          return {
            ...floor,
            rooms: floor.rooms.map((room, ri) => {
              if (ri !== roomIndex) return room;

              const newBeds = Array(numberOfBeds)
                .fill(null)
                .map((_, bedIndex) => ({
                  id: `${floor.id}${String.fromCharCode(65 + roomIndex)}${
                    bedIndex + 1
                  }`,
                  resident: null,
                }));

              return {
                ...room,
                beds: newBeds,
              };
            }),
          };
        }),
      };

      return { ...prev, houses: newHouses };
    });
  };

  const toTitleCase = (str) => {
    return str.replace(
      /\w\S*/g,
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );
  };

  const handleHouseChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      houses: prev.houses.map((house, i) =>
        i === index
          ? { ...house, [field]: field === "name" ? toTitleCase(value) : value }
          : house
      ),
    }));
  };

  const getDefaultColor = (index) => {
    // Array of professional, distinct colors for houses
    const defaultColors = [
      "#FF5A5F", // coral red
      "#00A699", // teal
      "#FC642D", // orange
      "#4F3CC9", // purple
      "#28A745", // green
      "#3B7EA1", // blue
      "#F7B731", // yellow
      "#7952B3", // violet
      "#E9406A", // pink
      "#00BCD4", // cyan
      "#C0392B", // dark red
      "#16A085", // emerald green
      "#F39C12", // tangerine
      "#8E44AD", // amethyst
      "#2ECC71", // light green
      "#3498DB", // bright blue
      "#E67E22", // carrot orange
      "#D35400", // pumpkin
      "#1ABC9C", // turquoise
      "#9B59B6", // wisteria
    ];

    // Return color based on index, cycling through the array if more houses than colors
    return defaultColors[index % defaultColors.length];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !user) return;

    try {
      setIsSubmitting(true);
      setError("");

      // Prepare organization data
      const organizationName = formData.organizationName;

      // FIRST: Check if the organization already exists
      const organizationRef = doc(db, "organizations", organizationName);
      const orgDoc = await getDoc(organizationRef);

      if (orgDoc.exists()) {
        setError(
          "An organization with this name already exists. Please choose a different name."
        );
        setIsSubmitting(false);
        return;
      }

      const housesWithColors = formData.houses.map((house, index) => ({
        ...house,
        color: house.color || getDefaultColor(index),
      }));

      // Get timezone
      const detectedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      console.log("Setting up organization:", organizationName);

      // STEP 1: Create initial user document FIRST before creating the organization
      // This is important to satisfy security rules
      const ownerUserRef = doc(
        db,
        "organizations",
        organizationName,
        "users",
        user.email
      );

      await setDoc(ownerUserRef, {
        email: user.email,
        role: "Owner",
        firstName: formData.firstName,
        lastName: formData.lastName,
        createdAt: new Date(),
      });
      console.log("Owner user document created successfully");

      // STEP 2: Now create the organization document
      await setDoc(organizationRef, {
        organizationName: formData.organizationName,
        ownerId: user.uid,
        createdAt: new Date(),
        timezone: detectedTimeZone,
      });
      console.log("Organization document created successfully");

      // STEP 3: Create House Settings document
      console.log("Creating houseSettings document...");
      const houseSettingsRef = doc(
        db,
        "organizations",
        organizationName,
        "houseSettings",
        "initial"
      );

      await setDoc(
        houseSettingsRef,
        {
          organizationName: formData.organizationName,
          moveInFee: parseFloat(formData.moveInFee),
          is90n90: formData.is90n90,
          houses: housesWithColors,
          created: new Date(),
          rentSchedule: formData.rentSchedule,
          rentDueDay: parseInt(formData.rentDueDay),
          rentDueDayName:
            formData.rentSchedule === "weekly"
              ? [
                  "Sunday",
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                ][parseInt(formData.rentDueDay)]
              : null,
          permissions: defaultPermissions,
          paidBedCount: getTotalBeds(housesWithColors),
          updatedAt: new Date(),
        },
        { merge: true }
      );
      console.log("houseSettings document created successfully");

      // Define collections to create
      const collections = [
        "invoices",
        "residents",
        "alumni",
        "waitlist",
        "activity",
      ];

      // Create collections first
      console.log("Creating collections...");
      for (const collectionName of collections) {
        try {
          const collectionRef = collection(
            db,
            "organizations",
            organizationName,
            collectionName
          );
          const initialDocRef = doc(collectionRef, "initial");
          await setDoc(initialDocRef, {
            created: new Date(),
            initialized: true,
          });

          // Delete the initial doc (we just create it to initialize the collection)
          await deleteDoc(initialDocRef);
          console.log(`Collection ${collectionName} initialized`);
        } catch (error) {
          console.error(
            `Error initializing collection ${collectionName}:`,
            error
          );
          // Continue with other collections despite errors
        }
      }

      // Create initial sobriety times for each house
      console.log("Creating sobriety times...");
      const sobrietyTimes = {};
      housesWithColors.forEach((house) => {
        sobrietyTimes[house.name] = { years: 0, months: 0, days: 0 };
      });

      // Record activity for setup completion
      console.log("Recording setup activity...");
      const activityCollectionRef = collection(
        db,
        "organizations",
        organizationName,
        "activity"
      );
      await setDoc(doc(activityCollectionRef, "setup-complete"), {
        type: "setup",
        user: `${formData.firstName} ${formData.lastName}`,
        description: "Organization setup completed",
        timestamp: new Date(),
        metadata: {
          houses: housesWithColors.length,
          totalBeds: getTotalBeds(housesWithColors),
        },
      });

      // Clean up pending user if applicable
      if (user.email) {
        console.log("Cleaning up pending user...");
        const pendingUserRef = doc(db, "pendingUsers", user.email);
        await deleteDoc(pendingUserRef);
      }

      // Set localStorage flags to prevent redirect loops
      localStorage.setItem("setupComplete", "true");
      localStorage.setItem("userOrg", organizationName);

      console.log("Setup completed successfully, redirecting to dashboard");
      // Use window.location.href for a full page reload instead of navigate
      window.location.href = "/";
    } catch (error) {
      console.error("Error setting up organization:", error);
      setError("Failed to setup organization: " + error.message);
      setIsSubmitting(false);
    }
  };

  if (isLoading) return null;

  return (
    <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950">
      {/* Ambient background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
      </div>

      <div className="relative min-h-screen">
        <header className="px-6 py-8">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <div className="flex gap-1">
              <div className="h-8 w-1 bg-gradient-to-b from-red-500 to-red-600 rounded-full animate-pulse" />
              <div className="h-8 w-1 bg-gradient-to-b from-red-500 to-red-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-8 w-1 bg-gradient-to-b from-red-500 to-red-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <span className="text-white font-bold text-xl">
              Recovery House Platform
            </span>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-6 pb-16">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">
              Welcome to Recovery House Platform
            </h1>
            <p className="text-lg text-slate-400">
              Let's get your organization set up in just a few steps
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Organization Details Card */}
            <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-800 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <Building2 className="text-red-500" size={24} />
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      Organization Details
                    </h2>
                    <p className="text-sm text-slate-400">
                      Tell us about your organization and yourself
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Personal Info Section */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-white">
                      First Name
                    </label>
                    <input
                      required
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter your first name"
                      className="w-full px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-white">
                      Last Name
                    </label>
                    <input
                      required
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Enter your last name"
                      className="w-full px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400"
                    />
                  </div>
                </div>

                {/* Organization Info */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-white">
                    Organization Name
                  </label>
                  <input
                    required
                    type="text"
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleInputChange}
                    placeholder="Enter your organization name"
                    className="w-full px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400"
                  />
                </div>

                {/* House Configuration */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-white">
                      Number of Houses
                    </label>
                    <div className="relative">
                      <input
                        required
                        type="number"
                        name="numberOfHouses"
                        value={formData.numberOfHouses}
                        onChange={handleInputChange}
                        min="1"
                        placeholder="1"
                        className="w-full px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400"
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        How many recovery houses do you operate?
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-white">
                      Move-in Fee
                    </label>
                    <div className="relative">
                      <input
                        required
                        type="number"
                        name="moveInFee"
                        value={formData.moveInFee}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0.00"
                        className="w-full px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400"
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        One-time fee charged at move-in
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label>Rent Schedule</label>
                    <select
                      required
                      name="rentSchedule"
                      value={formData.rentSchedule}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white"
                    >
                      <option value="">Select schedule</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label>Due Date</label>
                    <select
                      required
                      name="rentDueDay"
                      value={formData.rentDueDay}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white"
                    >
                      <option value="">Select due date</option>
                      {formData.rentSchedule === "weekly" ? (
                        <>
                          <option value="0">Sunday</option>
                          <option value="1">Monday</option>
                          <option value="2">Tuesday</option>
                          <option value="3">Wednesday</option>
                          <option value="4">Thursday</option>
                          <option value="5">Friday</option>
                          <option value="6">Saturday</option>
                        </>
                      ) : (
                        Array.from({ length: 31 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                {/* Meeting Requirements */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-white">
                    Meeting Requirements
                  </label>
                  <div className="space-y-3 bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="is90n90"
                        checked={formData.is90n90}
                        onChange={handleInputChange}
                        className="w-4 h-4 rounded border-slate-700 text-red-500 focus:ring-red-500"
                      />
                      <span className="text-white">
                        Require 90 meetings in 90 days for new residents
                      </span>
                    </div>
                    <div className="space-y-2">
                      <input
                        type="text"
                        name="meetingRequirements"
                        value={formData.meetingRequirements}
                        onChange={handleInputChange}
                        placeholder={
                          formData.is90n90
                            ? "Number of meetings required per week after 90/90"
                            : "Number of meetings required per week"
                        }
                        className="w-full px-4 py-3 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400"
                      />
                      <p className="text-xs text-slate-400">
                        {formData.is90n90
                          ? "Specify the weekly meeting requirement after completing 90/90"
                          : "Specify the standard weekly meeting requirement for all residents"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Houses Configuration */}
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Home className="text-red-500" size={24} />
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      Houses Configuration
                    </h2>
                    <p className="text-sm text-slate-400">
                      Set up the details for each recovery house
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {formData.houses.map((house, houseIndex) => (
                  <div
                    key={houseIndex}
                    className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-800 overflow-hidden"
                  >
                    <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-white">
                        House {houseIndex + 1}
                      </h3>
                      <div className="flex items-center gap-3">
                        <div className="space-y-1">
                          <label className="text-xs text-slate-400">
                            House Color
                          </label>
                          <input
                            type="color"
                            value={house.color || getDefaultColor(houseIndex)}
                            onChange={(e) =>
                              handleHouseChange(
                                houseIndex,
                                "color",
                                e.target.value
                              )
                            }
                            className="block w-20 h-8 rounded cursor-pointer bg-slate-800 border border-slate-700"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="p-6 space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-white">
                            House Name
                          </label>
                          <input
                            required
                            type="text"
                            value={house.name}
                            onChange={(e) =>
                              handleHouseChange(
                                houseIndex,
                                "name",
                                e.target.value
                              )
                            }
                            placeholder="Enter house name"
                            className="w-full px-4 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-white">
                            {formData.rentSchedule === "monthly"
                              ? "Monthly Fee"
                              : "Weekly Fee"}
                          </label>
                          <input
                            required
                            type="number"
                            value={house.fee}
                            onChange={(e) =>
                              handleHouseChange(
                                houseIndex,
                                "fee",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            min="0"
                            placeholder="0.00"
                            className="w-full px-4 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400"
                          />
                        </div>
                      </div>

                      {/* Floor Configuration */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                          <label className="text-sm font-medium text-white">
                            Number of Floors
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={house.floors.length}
                            onChange={(e) =>
                              handleFloorChange(
                                houseIndex,
                                parseInt(e.target.value) || 1
                              )
                            }
                            className="w-20 px-3 py-2 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white text-center"
                          />
                        </div>

                        {house.floors.map((floor, floorIndex) => (
                          <div
                            key={floorIndex}
                            className="bg-slate-800/50 rounded-lg"
                          >
                            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50">
                              <h4 className="text-white font-medium">
                                Floor {floor.id}
                              </h4>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-slate-400">
                                  Rooms:
                                </span>
                                <input
                                  type="number"
                                  min="1"
                                  value={floor.rooms.length}
                                  onChange={(e) =>
                                    handleRoomChange(
                                      houseIndex,
                                      floorIndex,
                                      parseInt(e.target.value) || 1
                                    )
                                  }
                                  className="w-20 px-3 py-2 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white text-center"
                                />
                              </div>
                            </div>

                            <div className="p-4 space-y-3">
                              {floor.rooms.map((room, roomIndex) => (
                                <div
                                  key={roomIndex}
                                  className="flex items-center space-x-4 p-3 bg-slate-900/20 rounded-lg"
                                >
                                  <input
                                    type="text"
                                    value={room.name}
                                    onChange={(e) => {
                                      const newRooms = [...floor.rooms];
                                      newRooms[roomIndex] = {
                                        ...room,
                                        name: e.target.value,
                                      };
                                      handleRoomChange(
                                        houseIndex,
                                        floorIndex,
                                        newRooms.length
                                      );
                                    }}
                                    placeholder="Room Name"
                                    className="flex-1 px-3 py-2 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400"
                                  />
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm text-slate-400">
                                      Beds:
                                    </span>
                                    <input
                                      type="number"
                                      min="1"
                                      value={room.beds.length}
                                      onChange={(e) =>
                                        handleBedChange(
                                          houseIndex,
                                          floorIndex,
                                          roomIndex,
                                          parseInt(e.target.value) || 1
                                        )
                                      }
                                      className="w-20 px-3 py-2 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white text-center"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full max-w-md mx-auto flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-300 font-medium text-lg"
              >
                {isSubmitting ? (
                  "Setting Up..."
                ) : (
                  <>
                    Complete Setup <ArrowRight size={20} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FirstTimeSetup;
